import React, { useState, useEffect } from "react";
import {
  Calendar,
  Plane,
  Hotel,
  User,
  FileText,
  Save,
  Edit2,
} from "lucide-react";
import { format } from "date-fns";
import {
  usePlayerDetailsByPlayerId,
  useUpdatePlayerDetailsByPlayerId,
  useCreatePlayerDetails,
} from "../../hooks/usePlayerDetails";
import {
  CreatePlayerDetailsRequest,
  UpdatePlayerDetailsRequest,
} from "../../api/services/playerDetails/types";

interface PlayerDetailsManagementProps {
  playerId: string;
  agentId?: string;
  onClose?: () => void;
  embedded?: boolean;
}

export const PlayerDetailsManagement: React.FC<
  PlayerDetailsManagementProps
> = ({ playerId, agentId, onClose, embedded = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<
    CreatePlayerDetailsRequest | UpdatePlayerDetailsRequest
  >({
    notes: "",
    agentId: agentId ? parseInt(agentId) : 0,
    playerId: parseInt(playerId),
    arrivalDate: new Date().toISOString(),
    arrivalTime: new Date().toISOString(),
    flightName: "",
    flightNumber: "",
    stayHotel: "",
    stayDuration: 0,
  });

  // Fetch existing player details
  const { data: playerDetails, isLoading } = usePlayerDetailsByPlayerId(
    parseInt(playerId),
    !!playerId,
  );

  // Mutations
  const createMutation = useCreatePlayerDetails();
  const updateMutation = useUpdatePlayerDetailsByPlayerId();

  // Populate form when data loads
  useEffect(() => {
    if (playerDetails) {
      setFormData({
        notes: playerDetails.notes || "",
        agentId: playerDetails.agentId,
        playerId: playerDetails.playerId,
        arrivalDate: playerDetails.arrivalDate,
        arrivalTime: playerDetails.arrivalTime,
        flightName: playerDetails.flightName || "",
        flightNumber: playerDetails.flightNumber || "",
        stayHotel: playerDetails.stayHotel || "",
        stayDuration: playerDetails.stayDuration || 0,
      });
    }
  }, [playerDetails]);

  const handleSave = async () => {
    try {
      if (playerDetails) {
        // Update existing details
        await updateMutation.mutateAsync({
          playerId: parseInt(playerId),
          data: formData as UpdatePlayerDetailsRequest,
        });
      } else {
        // Create new details
        await createMutation.mutateAsync(
          formData as CreatePlayerDetailsRequest,
        );
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving player details:", error);
    }
  };

  const formatDateTime = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "MMM dd, yyyy 'at' h:mm a");
    } catch {
      return "N/A";
    }
  };

  const formatDateOnly = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "yyyy-MM-dd");
    } catch {
      return "";
    }
  };

  const formatTimeOnly = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "HH:mm");
    } catch {
      return "";
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const containerClass = embedded
    ? "bg-slate-800 rounded-lg p-6"
    : "fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50";

  const content = (
    <div
      className={
        embedded
          ? ""
          : "bg-slate-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      }
    >
      {/* Header */}
      <div className={embedded ? "mb-6" : "p-6 border-b border-slate-700"}>
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-white flex items-center">
            <User className="w-5 h-5 mr-2" />
            Player Travel & Accommodation Details
          </h2>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="text-blue-400 hover:text-blue-300"
            >
              <Edit2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className={embedded ? "space-y-6" : "p-6 space-y-6"}>
        {isEditing ? (
          // Edit Mode
          <>
            <div className="grid grid-cols-2 gap-6">
              {/* Flight Information */}
              <div className="space-y-4">
                <h3 className="text-md font-medium text-white flex items-center">
                  <Plane className="w-4 h-4 mr-2" />
                  Flight Information
                </h3>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Flight Name
                  </label>
                  <input
                    type="text"
                    value={formData.flightName || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, flightName: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Emirates"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Flight Number
                  </label>
                  <input
                    type="text"
                    value={formData.flightNumber || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, flightNumber: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., EK123"
                  />
                </div>
              </div>

              {/* Arrival Information */}
              <div className="space-y-4">
                <h3 className="text-md font-medium text-white flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  Arrival Information
                </h3>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Arrival Date
                  </label>
                  <input
                    type="date"
                    value={formatDateOnly(formData.arrivalDate || "")}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        arrivalDate: new Date(e.target.value).toISOString(),
                      })
                    }
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Arrival Time
                  </label>
                  <input
                    type="time"
                    value={formatTimeOnly(formData.arrivalTime || "")}
                    onChange={(e) => {
                      const [hours, minutes] = e.target.value.split(":");
                      const date = new Date(formData.arrivalDate || new Date());
                      date.setHours(parseInt(hours), parseInt(minutes));
                      setFormData({
                        ...formData,
                        arrivalTime: date.toISOString(),
                      });
                    }}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Hotel Information */}
              <div className="space-y-4">
                <h3 className="text-md font-medium text-white flex items-center">
                  <Hotel className="w-4 h-4 mr-2" />
                  Accommodation
                </h3>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Hotel Name
                  </label>
                  <input
                    type="text"
                    value={formData.stayHotel || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, stayHotel: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Marriott Hotel"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Stay Duration (Days)
                  </label>
                  <input
                    type="number"
                    value={formData.stayDuration || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        stayDuration: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 5"
                    min="0"
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-4">
                <h3 className="text-md font-medium text-white flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  Additional Notes
                </h3>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    rows={4}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Special requirements, preferences, etc."
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          // View Mode
          <>
            <div className="grid grid-cols-2 gap-6">
              {/* Flight Information */}
              <div className="space-y-3">
                <h3 className="text-md font-medium text-white flex items-center mb-4">
                  <Plane className="w-4 h-4 mr-2" />
                  Flight Information
                </h3>

                <div>
                  <p className="text-sm text-slate-400 mb-1">Flight</p>
                  <p className="text-white">
                    {playerDetails?.flightName && playerDetails?.flightNumber
                      ? `${playerDetails.flightName} - ${playerDetails.flightNumber}`
                      : "Not specified"}
                  </p>
                </div>
              </div>

              {/* Arrival Information */}
              <div className="space-y-3">
                <h3 className="text-md font-medium text-white flex items-center mb-4">
                  <Calendar className="w-4 h-4 mr-2" />
                  Arrival Information
                </h3>

                <div>
                  <p className="text-sm text-slate-400 mb-1">
                    Arrival Date & Time
                  </p>
                  <p className="text-white">
                    {playerDetails?.arrivalDate
                      ? formatDateTime(playerDetails.arrivalDate)
                      : "Not specified"}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Hotel Information */}
              <div className="space-y-3">
                <h3 className="text-md font-medium text-white flex items-center mb-4">
                  <Hotel className="w-4 h-4 mr-2" />
                  Accommodation
                </h3>

                <div>
                  <p className="text-sm text-slate-400 mb-1">Hotel</p>
                  <p className="text-white">
                    {playerDetails?.stayHotel || "Not specified"}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-slate-400 mb-1">Duration</p>
                  <p className="text-white">
                    {playerDetails?.stayDuration
                      ? `${playerDetails.stayDuration} days`
                      : "Not specified"}
                  </p>
                </div>
              </div>

              {/* Notes */}
              {playerDetails?.notes && (
                <div className="space-y-3">
                  <h3 className="text-md font-medium text-white flex items-center mb-4">
                    <FileText className="w-4 h-4 mr-2" />
                    Notes
                  </h3>

                  <div>
                    <p className="text-white whitespace-pre-wrap">
                      {playerDetails.notes}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Actions */}
      <div
        className={
          embedded
            ? "mt-6 flex gap-3"
            : "flex gap-3 p-6 border-t border-slate-700"
        }
      >
        {isEditing ? (
          <>
            <button
              onClick={() => setIsEditing(false)}
              className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              <Save className="w-4 h-4 mr-2" />
              {createMutation.isPending || updateMutation.isPending
                ? "Saving..."
                : "Save Details"}
            </button>
          </>
        ) : (
          !embedded && (
            <button
              onClick={onClose}
              className="w-full px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
            >
              Close
            </button>
          )
        )}
      </div>
    </div>
  );

  return embedded ? content : <div className={containerClass}>{content}</div>;
};
